import { Box, Divider, Grow, List, Popper } from '@mui/material';
import React from 'react';
import LinkIcon from '@mui/icons-material/Link';
import ProxyIcon from '@mui/icons-material/ControlPointDuplicate';
import NoteIcon from '@mui/icons-material/DescriptionOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import HideIcon from '@mui/icons-material/VisibilityOffOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ResetPositionIcon from '@mui/icons-material/RestartAlt';
import MigrateIcon from '@mui/icons-material/DriveFileMoveOutlined';
import MergeIcon from '@mui/icons-material/MergeType';
import AddRelativeMenu from './options/AddRelativeMenu';
import GenderMenu from './options/GenderMenu';
import AdoptedMenu from './options/AdoptedMenu';
import FertilityMenu from './options/FertilityMenu';
import EditText from './options/EditText';
import Comment from './options/Comment';
import SetRelation from './options/SetRelation';
import { createProxyFor, removeProxyFor } from './options/CreateProxy';
import DeleteNode from './options/DeleteNode';
import HidePerson from './options/HidePerson';
import ResetPosition from './options/ResetPosition';
import MakeProband from './options/MakeProband';
import RemoveProband from './options/RemoveProband';
import ReproductiveMenu from './options/ReproductiveMenu';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { getProbandState } from '../pedigree/probandState';
import { canDeleteNode } from '../pedigree/nodeActionGates';
import { reproMenuPolicy } from './reproMenuPolicy';
import MenuItemRow from './MenuItemRow';
import {
  AdoptedIcon,
  FertilityIcon,
  GenderIcon,
  ProbandIcon,
  ReproIcon,
  SpouseIcon
} from './MenuIcons';
import { MENU_TONE, MENU_WIDTH, SUBMENU_MODIFIERS } from './menuTheme';

function ContextMenu({
  user,
  node,
  setMemberRecords,
  memberRecords,
  onCloseMenu,
  setPedigreeState,
  selectionModeRef,
  handleSelectionExit,
  setError,
  customPositions,
  setCustomPositions,
  selectedCount,
  onMergeTwoSelected,
  onAddProxy,
  onRemoveProxy,
  eligiblePids,
  onMigrate,
  isSaving = false,
  onHistoryCheckpoint = null,
  viewHide = null,
  hideAnalysisMembers = null
}) {
  const isMigratable = !!eligiblePids?.has(String(node?.pid));
  // Tri-state proband read (see pedigree/probandState.js): 'explicit'
  // promoted member, 'cleared' (no active proband → probandPid null), or
  // 'legacy' structural-root fallback. Deriving activeProbandPid from the
  // same selector Canvas uses keeps arrow presence ⇔ Remove Proband
  // availability in lockstep across all three states. NOTE (D2, accepted):
  // this makes virtually every root read as the current proband, so Delete
  // Person on the root is a deliberate two-step flow (move or remove the
  // proband first).
  const activeProbandPid = React.useMemo(
    () => getProbandState(memberRecords).probandPid,
    [memberRecords]
  );
  const isCurrentProband =
    activeProbandPid != null && String(node?.pid) === String(activeProbandPid);
  // Same rule the keyboard Delete shortcut applies — see pedigree/nodeActionGates.js.
  const deleteGate = canDeleteNode(node, memberRecords);
  // Per-node action policy (Bennett repro nodes restrict the menu + relabel Delete/Hide/
  // Note by node type). Ordinary nodes + flag-off → full menu with "Person" labels.
  const policy = reproMenuPolicy(node);
  const [subMenu, setSubMenu] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  React.useEffect(() => {
    setSubMenu(false);
  }, [node]);

  const toggleSubMenu = (e, { val }) => {
    setAnchorEl(e.currentTarget);
    setSubMenu({ val });
  };

  const menu = React.useMemo(() => {
    switch (subMenu.val) {
      case 1:
        return (
          <AddRelativeMenu
            node={node}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            onCloseMenu={onCloseMenu}
            setPedigreeState={setPedigreeState}
            user={user}
            // Adds stay disabled for the WHOLE save round trip (fire →
            // response-merge): a click landing mid-flight used to get its
            // structural edits reverted by the response reconciliation.
            isDisabled={isSaving}
          />
        );

      case 2:
        return (
          <SetRelation
            node={node}
            user={user}
            handleClose={onCloseMenu}
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            setPedigreeState={setPedigreeState}
            selectionModeRef={selectionModeRef}
            handleSelectionExit={handleSelectionExit}
            setError={setError}
          />
        );

      case 3:
        return (
          <GenderMenu
            node={node}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
          />
        );

      case 4:
        return (
          <AdoptedMenu
            node={node}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
          />
        );

      case 5:
        return (
          <FertilityMenu
            node={node}
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
          />
        );

      case 6:
        return (
          <Comment
            node={node}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
          />
        );

      case 7:
        return (
          <EditText
            node={node}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
          />
        );

      case 9:
        return (
          <DeleteNode
            node={node}
            user={user}
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            handleClose={onCloseMenu}
          />
        );

      case 10:
        return (
          <HidePerson
            node={node}
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
            viewHide={viewHide}
            analysisMembers={hideAnalysisMembers}
          />
        );

      case 11:
        return (
          <ResetPosition
            node={node}
            customPositions={customPositions}
            setCustomPositions={setCustomPositions}
            handleClose={onCloseMenu}
            onHistoryCheckpoint={onHistoryCheckpoint}
          />
        );

      case 12:
        return (
          <MakeProband
            node={node}
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
            userId={user?._id}
            isSaving={isSaving}
          />
        );

      case 13:
        return (
          <RemoveProband
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
            userId={user?._id}
            isSaving={isSaving}
          />
        );

      case 14:
        return (
          <ReproductiveMenu
            node={node}
            user={user}
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
          />
        );

      default:
        return false;
    }
  }, [
    subMenu,
    node,
    memberRecords,
    user,
    customPositions,
    setCustomPositions,
    setMemberRecords,
    setPedigreeState,
    setError,
    onCloseMenu,
    onAddProxy,
    onRemoveProxy,
    isSaving
  ]);

  // The card every menu shape sits in. One definition, so the Merge/Migrate,
  // multi-select and proxy menus cannot drift away from the main one — they used
  // to be bare ListItemButtons in a 170px box with a #777 hairline, which after
  // this redesign would have looked like a different product (TGCLOUD-1644).
  const shell = (children) => (
    <ClickAwayListener onClickAway={onCloseMenu}>
      <Box sx={{ position: 'relative', zIndex: 1000 }}>
        {node && (
          <Grow in={node} style={{ transformOrigin: 'top bottom' }}>
            <Box
              sx={{
                width: MENU_WIDTH,
                bgcolor: 'background.paper',
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 3,
                margin: 1,
                overflow: 'hidden'
              }}
            >
              <List disablePadding>{children}</List>
            </Box>
          </Grow>
        )}

        <Popper
          open={!!subMenu && !!node}
          anchorEl={anchorEl}
          placement="right-start"
          sx={{ zIndex: 1200 }}
          // Without these a flyout opened near the right or bottom edge of the
          // window runs off it. The root menu has the same modifiers applied
          // where ChartBox mounts it.
          modifiers={SUBMENU_MODIFIERS}
        >
          <Grow in={node} style={{ transformOrigin: 'top bottom' }}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 3
              }}
            >
              {menu}
            </Box>
          </Grow>
        </Popper>
      </Box>
    </ClickAwayListener>
  );

  if (selectedCount > 2) {
    return shell(
      <MenuItemRow
        icon={<MigrateIcon fontSize="small" />}
        tone={MENU_TONE.neutral}
        label={`Migrate (${selectedCount})`}
        onClick={() => {
          onMigrate?.(node);
          onCloseMenu?.();
        }}
      />
    );
  }

  if (selectedCount === 2) {
    return shell(
      <>
        <MenuItemRow
          icon={<MergeIcon fontSize="small" />}
          tone={MENU_TONE.relationship}
          label="Merge"
          onMouseEnter={() => setSubMenu(false)}
          onClick={() => {
            onMergeTwoSelected?.();
            onCloseMenu?.();
          }}
        />
        <MenuItemRow
          icon={<MigrateIcon fontSize="small" />}
          tone={MENU_TONE.neutral}
          label="Migrate (2)"
          onMouseEnter={() => setSubMenu(false)}
          onClick={() => {
            onMigrate?.(node);
            onCloseMenu?.();
          }}
        />
      </>
    );
  }

  if (node?.isProxy) {
    return shell(
      <MenuItemRow
        icon={<ProxyIcon fontSize="small" />}
        tone={MENU_TONE.relationship}
        label="Remove Proxy"
        onClick={() => {
          if (removeProxyFor({ node, onRemoveProxy, setError }))
            onCloseMenu?.();
        }}
      />
    );
  }

  // ---------------------------------------------------------------- the groups
  //
  // Four groups, hairline separators, no section labels and no tinted bands. A
  // labelled-and-tinted variant was mocked and rejected during design: it
  // measured 550px against 464px, and a taller menu is likelier to flip or clip
  // near the canvas edge.
  //
  // Colour marks the GROUP and lives on the icon only — see menuTheme.js.
  const groups = [
    {
      key: 'relationships',
      // Everything that adds a node or changes an edge.
      items: [
        policy.allow.addRelation && {
          key: 'add',
          val: 1,
          label: 'Add Relative',
          icon: <SpouseIcon />,
          tone: MENU_TONE.relationship,
          submenu: true
        },
        policy.allow.setRelation && {
          key: 'link',
          val: 2,
          label: 'Link to Existing…',
          icon: <LinkIcon fontSize="small" />,
          tone: MENU_TONE.relationship,
          submenu: true
        },
        // Grouped here rather than beside Fertility because every item behind it
        // CREATES something — a pregnancy node, a donor edge. It is an add menu
        // wearing another name, which is the same reasoning that put the fetus
        // and donor launchers inside Add Relative in the sidebar.
        policy.allow.reproductive && {
          key: 'repro',
          val: 14,
          label: 'Reproductive',
          icon: <ReproIcon />,
          tone: MENU_TONE.reproductive,
          submenu: true
        },
        // Direct, not a submenu: the flyout behind this held exactly ONE button.
        policy.allow.proxy && {
          key: 'proxy',
          label: 'Proxy Person',
          icon: <ProxyIcon fontSize="small" />,
          tone: MENU_TONE.relationship,
          onClick: () => {
            if (createProxyFor({ node, memberRecords, onAddProxy, setError }))
              onCloseMenu?.();
          }
        }
      ]
    },
    {
      key: 'person',
      // Attributes that change how the EXISTING symbol is drawn — a mark added
      // to something already on the chart, never a new node.
      items: [
        policy.allow.gender && {
          key: 'gender',
          val: 3,
          label: 'Gender',
          icon: <GenderIcon />,
          tone: MENU_TONE.person,
          submenu: true
        },
        policy.allow.adopted && {
          key: 'adopted',
          val: 4,
          label: 'Adopted',
          icon: <AdoptedIcon />,
          tone: MENU_TONE.person,
          submenu: true
        },
        policy.allow.fertility && {
          key: 'fertility',
          val: 5,
          label: 'Fertility',
          icon: <FertilityIcon />,
          tone: MENU_TONE.person,
          submenu: true
        }
      ]
    },
    {
      key: 'annotate',
      // Free text on the node, not clinical structure.
      items: [
        policy.allow.note && {
          key: 'note',
          val: 6,
          label: `${policy.nounLabel} Note`,
          icon: <NoteIcon fontSize="small" />,
          tone: MENU_TONE.neutral
        },
        policy.allow.editText && {
          key: 'editText',
          val: 7,
          label: 'Edit Text',
          icon: <EditIcon fontSize="small" />,
          tone: MENU_TONE.neutral
        }
      ]
    },
    {
      key: 'node',
      // Acting on the node rather than on the person's data. The only
      // variable-length group, which is why it sits at the bottom.
      items: [
        policy.allow.resetPosition &&
          customPositions?.has(node?.uid) && {
            key: 'resetPosition',
            val: 11,
            label: 'Reset Position',
            icon: <ResetPositionIcon fontSize="small" />,
            tone: MENU_TONE.neutral
          },
        policy.allow.hide && {
          key: 'hide',
          val: 10,
          label: `Hide ${policy.nounLabel}`,
          icon: <HideIcon fontSize="small" />,
          tone: MENU_TONE.neutral
        },
        // Proband changes stay disabled for the WHOLE save round trip (fire →
        // response-merge), mirroring Add Relative: a promote/clear fired
        // mid-flight races the in-flight config save and could clobber the
        // designation it just wrote.
        policy.allow.makeProband && {
          key: 'proband',
          val: isCurrentProband ? 13 : 12,
          label: isCurrentProband ? 'Remove Proband' : 'Make Proband',
          icon: <ProbandIcon />,
          tone: MENU_TONE.person,
          disabled: isSaving,
          hint: isSaving ? 'Saving…' : ''
        },
        policy.allow.migrate &&
          isMigratable && {
            key: 'migrate',
            label: 'Migrate Person',
            icon: <MigrateIcon fontSize="small" />,
            tone: MENU_TONE.neutral,
            onClick: () => {
              onMigrate?.(node);
              onCloseMenu?.();
            }
          }
      ]
    },
    {
      key: 'delete',
      // Its own group so it can never sit flush against Make Proband with the
      // same weight and hit area as Edit Text. On a clinical record the
      // separator is as much of the guard as the colour is.
      items: [
        policy.allow.del &&
          deleteGate.allowed && {
            key: 'delete',
            val: 9,
            label: `Delete ${policy.nounLabel}`,
            icon: <DeleteIcon fontSize="small" />,
            tone: MENU_TONE.danger
          }
      ]
    }
  ]
    .map((g) => ({ ...g, items: g.items.filter(Boolean) }))
    .filter((g) => g.items.length);

  return shell(
    groups.map((group, groupIndex) => (
      <React.Fragment key={group.key}>
        {groupIndex > 0 && <Divider sx={{ my: 0.5 }} />}
        {group.items.map((item) => (
          <MenuItemRow
            key={item.key}
            icon={item.icon}
            tone={item.tone}
            label={item.label}
            hint={item.hint}
            disabled={item.disabled}
            hasSubmenu={!!item.submenu}
            selected={item.val != null && subMenu?.val === item.val}
            onMouseEnter={
              item.submenu
                ? (e) => toggleSubMenu(e, { val: item.val })
                : () => setSubMenu(false)
            }
            onClick={
              item.onClick || ((e) => toggleSubMenu(e, { val: item.val }))
            }
          />
        ))}
      </React.Fragment>
    ))
  );
}

export default ContextMenu;
